import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Auth from '@Services/Auth';
import { FEATURES } from '@Constants/rx-ui/constants';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import DocumentCustomLinkComponent from '@Components/Results/documents/document-custom-link.component';
import FullTextComponent from '@Components/Results/reactions/reaction-details/full-text.component';
import { displayConditions } from '@Components/Results/documents/DocumentExpandSections';

function DocumentButtonsComponent({ document: citation, toggleSection, getTrackingAttrs, metadata, ...props }) {
  const generateButton = (flag, label, count, customAction) => {
    const trackingAttrs = getTrackingAttrs({
      'data-tracking': !props[flag],
      'data-tracking-type': 'contentview,contentlinkclick',
      'data-tracking-action': label,
    });
    const onClick = customAction ? (e) => customAction(e, flag, toggleSection) : (e) => toggleSection(e, flag);
    return (
      <button
        type="button"
        className={cn(
          'els-btn els-btn-link els-btn-xs blue-compact els-btn-right e2e-action',
          `e2e-document-action-${label.toLowerCase()}`
        )}
        onClick={onClick}
        {...trackingAttrs}>
        <span>{label}</span>
        {count && <span className="rx-count-badge">{count}</span>}
        <svg className="els-btn-icon">
          <use xlinkHref={props[flag] ? '#icon-chevron-up' : '#icon-chevron-down'} />
        </svg>
      </button>
    );
  };

  return (
    <>
      <div className="result-detail-row-btns">
        <div className="els-btn-group-left">
          {/* Abstract */}
          {displayConditions.abstractExpanded(citation) && generateButton('abstractExpanded', 'Abstract')}
          {/* Index Terms */}
          {displayConditions.indextermsExpanded(citation) && generateButton('indextermsExpanded', 'Index Terms')}
          {/* Claims */}
          {displayConditions.claimsExpanded(citation) && generateButton('claimsExpanded', 'Claims')}
          {/* Bibliographic Info */}
          {displayConditions.bibliographicInfoExpanded(citation) &&
            generateButton(
              'bibliographicInfoExpanded',
              !Auth.isFeatureEnabled(FEATURES.disablePatentSightUIChanges) ? 'Bibliographic Info' : 'Front Page Info'
            )}
          {/* Substances */}
          {displayConditions.substancesExpanded(citation) && generateButton('substancesExpanded', 'Substances', citation.substancesCount)}
          {/* Reactions */}
          {displayConditions.reactionsExpanded(citation) && generateButton('reactionsExpanded', 'Reactions', citation.reactionsCount)}
          {/* Targets */}
          {displayConditions.targetsExpanded(citation) && generateButton('targetsExpanded', 'Targets')}
          {/* Full Text */}
          {citation.fullTextCustom && <DocumentCustomLinkComponent citation={citation} getTrackingAttrs={getTrackingAttrs} />}
          {!citation.fullTextCustom && (
            <FullTextComponent
              citation={citation}
              trackingAttributes={{
                index: citation.index,
                getTrackingAttrs: getTrackingAttrs(),
              }}
              metadata={metadata}
            />
          )}
        </div>
      </div>
      {(displayConditions.hitSubstancesExpanded(citation) || displayConditions.hitReactionsExpanded(citation)) && (
        <div className="result-detail-row-btns">
          <div className="els-btn-group-left">
            {/* Hit Substances */}
            {displayConditions.hitSubstancesExpanded(citation) &&
              generateButton('hitSubstancesExpanded', 'Hit Substances', citation.substancesHitCount)}
            {/* Hit Reactions */}
            {displayConditions.hitReactionsExpanded(citation) &&
              generateButton('hitReactionsExpanded', 'Hit Reactions', citation.reactionsHitCount)}
          </div>
        </div>
      )}
    </>
  );
}

DocumentButtonsComponent.propTypes = {
  document: PropTypes.object.isRequired,
  toggleSection: PropTypes.func.isRequired,
  abstractExpanded: PropTypes.bool.isRequired,
  claimsExpanded: PropTypes.bool.isRequired,
  indextermsExpanded: PropTypes.bool.isRequired,
  bibliographicInfoExpanded: PropTypes.bool.isRequired,
  substancesExpanded: PropTypes.bool.isRequired,
  reactionsExpanded: PropTypes.bool.isRequired,
  targetsExpanded: PropTypes.bool.isRequired,
  hitSubstancesExpanded: PropTypes.bool.isRequired,
  hitReactionsExpanded: PropTypes.bool.isRequired,
  getTrackingAttrs: PropTypes.func.isRequired,
  metadata: MetadataPropTypesShape,
};

export default DocumentButtonsComponent;
